import { noop } from 'lodash-es';
import { systemLib } from 'shared';

import { FlowType } from '../../../shared/config';
import { tryNativeMethod, connect, debug } from './communication-helpers';
import {
  addAuthSuccessEventListener,
  registerAuthSuccessEventEmitter,
} from './native-events';
import type { SupportedMiniApps } from '../config';
import type {
  SuccessfulPaymentAction,
  AmplitudeEventPayload,
  AcceleraEventPayload,
  AppName,
} from '../types';

/**
 * Native product identifier as expected by `window.FreedomApp.getProductName`.
 * Note: avia is sent as 'TRAVEL' (legacy native naming), not 'AVIA'.
 */
export type NativeProductName = 'TRAVEL' | 'RAILWAYS' | 'TOURS';

declare global {
  interface Window {
    FreedomApp: {
      /**
       * Method to upload file to native app
       * @param filePayload - JSON of { base64File: string, fileType: string }
       */
      openFile: (filePayload: string) => void;
      /**
       * Method to init payment process
       * @param paymentPayload - JSON of { billId: string, orderId: string, orderNumber: string }
       */
      pay: (paymentPayload: string) => void;
      authorize: (appName: AppName) => void;
      backToApp: () => void;
      /**
       * Close the splash screen when the application is fully loaded. Only for iOS, since Android closes by itself.
       */
      closeSplashScreen: () => void;
      /**
       * Method to open device settings
       */
      openSettings: () => void;
      /**
       * Method to open link in external browser
       * @param link - URL to open
       */
      openInBrowser: (link: string) => void;
      /**
       * Method to send event to AppsFlyer
       * @param eventName - name of the event
       * @param payload - payload of the event
       */
      sendAppsFlyerEvent: (eventName: string, payload: string) => void;
      /**
       * Remove webview's cache from mobile application.
       * HTML, JS, CSS, WEB, WOFF2
       */
      clearCache: () => void;
      /**
       * Remove cookies cache from mobile application
       */
      clearCookies: () => void;
      /**
       * Send event to native Amplitude
       * @param payload
       */
      sendAmplitudeEvent: (payload: string) => void;
      /**
       * Send event to Kafka (Accelera bank integration)
       * @param payload - JSON string of AcceleraEventPayload
       */
      sendKafkaEvent: (payload: string) => void;
      /**
       * NavigateInfo to specified URL in super app
       * @param url - URL to navigate to
       */
      navigate: (url: string) => void;
      /**
       * Method to share content
       * @note Only available on Android
       */
      share: (url: string) => void;
      /**
       * Notify the native app which product the user is currently in.
       * Called on init for product apps and on product switch inside
       * the switcher apps (cabinet, passengers).
       * @param appName - native product identifier ('TRAVEL' for avia, otherwise uppercase)
       */
      getProductName: (appName: NativeProductName) => void;
    };
    FreedomWebApp: {
      onAuthSuccess: (otpToken: string) => void;
      onAuthError: () => void;
      onPaymentSuccess: (action: SuccessfulPaymentAction) => void;
      onPaymentError: () => void;
      onNativeEventError?: (error: Error) => void;
    };
    /**
     *  webkit - существует только для ios webview. При необходимости большей информации гуглить: `WKScriptMessageHandler`
     *  * */
    webkit?: {
      messageHandlers?: {
        /**
         *  @description changeThemeColor - метод для смены темы(полоска сверху) на ios, нативка со своей стороны слушает этот метод и меняет цвет темы.
         *  @param message - в данном случае это цвет. Например: #F1F1F1.
         * */
        changeThemeColor?: {
          postMessage: (message: string) => void;
        };
      };
    };
  }
}

const promiseNativeEventErrorHandler = (
  error: Error,
  reject: (reason: Error) => void,
) => {
  if (window.FreedomWebApp.onNativeEventError) {
    window.FreedomWebApp.onNativeEventError(error);
  }

  reject(error);
};

/**
 * Need to call this method on app init to setup FreedomWebApp with empty methods.
 * Otherwise, TypeError "Cannot set properties of undefined (setting 'onAuthSuccess')",
 * since window.FreedomWebApp is undefined
 */
export function setupEmptyWebViewApi() {
  window.FreedomWebApp = {
    /* eslint-disable @typescript-eslint/no-empty-function */
    onAuthSuccess() {},
    onAuthError() {},
    onPaymentSuccess() {},
    onPaymentError() {},
    /* eslint-enable @typescript-eslint/no-empty-function */
  };
}

/**
 * Map FlowType ('av'/'rw'/'tr'/'b2b') to the AppName the native bridge
 * expects ('avia'/'railways'/'tours'). Returns null for B2B and any
 * unsupported value since B2B is not a product surface on the native side.
 */
export function flowTypeToAppName(flowType: FlowType): AppName | null {
  switch (flowType) {
    case FlowType.AVIA:
      return 'avia';
    case FlowType.RAILWAYS:
      return 'railways';
    case FlowType.TOURS:
      return 'tours';
    default:
      return null;
  }
}

/**
 * Notify the native app which product the user is currently viewing.
 * Product apps should call this once at startup; switcher apps (cabinet,
 * passengers) should call this on initial mount and on every product switch.
 */
const APP_NAME_TO_NATIVE_PRODUCT_NAME: Record<AppName, NativeProductName> = {
  avia: 'TRAVEL',
  railways: 'RAILWAYS',
  tours: 'TOURS',
};

export function getProductName(appName: AppName) {
  const nativeProductName = APP_NAME_TO_NATIVE_PRODUCT_NAME[appName];

  tryNativeMethod({
    callback: () => window.FreedomApp.getProductName(nativeProductName),
    errorMessage:
      'Native app does not implement `window.FreedomApp.getProductName` method',
    /**
     * Swallow the error so older native builds without this method don't
     * crash the app at boot. The call is informational; if native can't
     * receive it, the app should still work.
     */
    errorHandler: error => debug('[getProductName]', error.message),
  });
}

export function methodsWithAppName(appName: FlowType) {
  const mappedAppName: AppName = flowTypeToAppName(appName) ?? 'avia';

  return {
    authorize() {
      return connect<string>((resolve, reject) => {
        tryNativeMethod({
          /**
           * Need to wrap native methods in function to prevent "Java bridge method can't be invoked on a non-injected object"
           * @see https://stackoverflow.com/questions/59158951/java-bridge-method-cant-be-invoked-on-a-non-injected-object
           */
          callback: () => window.FreedomApp.authorize(mappedAppName),
          errorMessage:
            'Native app does not implement `window.FreedomApp.authorize` method',
          errorHandler: (error: Error) => {
            promiseNativeEventErrorHandler(error, reject);
          },
        });

        registerAuthSuccessEventEmitter();
        addAuthSuccessEventListener(mappedAppName, otpToken =>
          resolve(otpToken),
        );

        window.FreedomWebApp.onAuthError = () =>
          reject(new Error('Native app authorization failed'));
      });
    },
    payByBillId(billId: string): Promise<SuccessfulPaymentAction> {
      return connect((resolve, reject) => {
        tryNativeMethod({
          callback: () =>
            window.FreedomApp.pay(
              JSON.stringify({ billId, appName: mappedAppName }),
            ),
          errorMessage:
            'Native app does not implement `window.FreedomApp.pay` method',
          errorHandler: (error: Error) => {
            promiseNativeEventErrorHandler(error, reject);
          },
        });

        window.FreedomWebApp.onPaymentSuccess = (
          action: SuccessfulPaymentAction,
        ) => resolve(action);
        window.FreedomWebApp.onPaymentError = () =>
          reject(new Error('ERR_NATIVE_PAYMENT_FAILED'));
      });
    },
  };
}

export function backToApp() {
  tryNativeMethod({
    callback: () => window.FreedomApp.backToApp(),
    errorMessage:
      'Native app does not implement `window.FreedomApp.backToApp` method',
  });
}

export function openBase64File(filePayload: string) {
  tryNativeMethod({
    callback: () => window.FreedomApp.openFile(filePayload),
    errorMessage:
      'Native app does not implement `window.FreedomApp.openFile` method',
  });
}

export function closeSplashScreen() {
  performance.mark('closeSplashScreen');

  tryNativeMethod({
    callback: () => window.FreedomApp.closeSplashScreen(),
    errorMessage:
      'Native app does not implement `window.FreedomApp.closeSplashScreen` method',
  });
}

/**
 * Opens device settings
 */
export function openSettings() {
  tryNativeMethod({
    callback: () => window.FreedomApp.openSettings(),
    errorMessage:
      'Native app does not implement `window.FreedomApp.openSettings` method',
  });
}

/**
 * Opens a link in external browser
 * @param {string} link - URL to open
 */
export function openInBrowser(link: string) {
  tryNativeMethod({
    callback: () => window.FreedomApp.openInBrowser(link),
    errorMessage:
      'Native app does not implement `window.FreedomApp.openInBrowser` method',
  });
}

export function clearCache() {
  try {
    tryNativeMethod({
      callback: () => window.FreedomApp.clearCache(),
      errorMessage:
        'Native app does not implement `window.FreedomApp.clearCache` method',
    });
  } catch (error) {
    if (
      error instanceof Error &&
      error.message !== 'Java exception was raised during method invocation'
    ) {
      noop();
    } else {
      throw error;
    }
  }
}

export function sendAppsFlyerEvent(
  eventName: string,
  payload: unknown,
  { logger }: { logger?: (error: Error) => void } = {},
) {
  debug('Sending event', eventName, 'to AppsFlyer with payload', payload);

  // Turn off for now, since mobile apps don't support this event
  if (import.meta.env.VITE_RUNTIME_ENV === 'development') {
    return;
  }

  let mappedPayload = payload;
  const isIos = systemLib.getMobileOperatingSystem() === 'ios';

  if (!isIos) {
    mappedPayload = JSON.stringify(payload);
  }

  tryNativeMethod({
    callback: () =>
      window.FreedomApp.sendAppsFlyerEvent(eventName, mappedPayload),
    errorMessage:
      'Native app does not implement `window.FreedomApp.sendAppsFlyerEvent` method',
    errorHandler: logger,
  });
}

export function sendAmplitudeEvent(
  payload: AmplitudeEventPayload,
  logger?: (error: Error) => void,
) {
  debug('Sending event', payload.name, 'to JS Bridge with payload', payload);

  if (import.meta.env.VITE_RUNTIME_ENV === 'development') {
    return;
  }

  tryNativeMethod({
    callback: () =>
      window.FreedomApp.sendAmplitudeEvent(JSON.stringify(payload)),
    errorMessage:
      'Native app does not implement `window.FreedomApp.sendAmplitudeEvent` method',
    errorHandler: logger,
  });
}

/**
 * Sends event to Kafka via Accelera bank integration
 * @param payload - Event payload following Accelera standards
 * @param logger - Optional error handler
 */
export function sendKafkaEvent(
  payload: AcceleraEventPayload,
  logger?: (error: Error) => void,
) {
  debug(
    'Sending event',
    payload.name,
    'to Kafka (Accelera) with payload',
    payload,
  );

  if (import.meta.env.VITE_RUNTIME_ENV === 'development') {
    return;
  }

  tryNativeMethod({
    callback: () => window.FreedomApp.sendKafkaEvent(JSON.stringify(payload)),
    errorMessage:
      'Native app does not implement `window.FreedomApp.sendKafkaEvent` method',
    errorHandler: logger,
  });
}

export function navigate(url: string) {
  tryNativeMethod({
    callback: () => window.FreedomApp.navigate(url),
    errorMessage:
      'Native app does not implement `window.FreedomApp.navigate` method',
  });
}

export function openMiniApp(miniApp: SupportedMiniApps, url: string) {
  const base = `https://bankffin.kz/services/${miniApp}`;
  const target = url ? `${base}?url=${url}` : base;
  navigate(target);
}

/**
 * Share content using native share or fallback to navigator.share
 * @note Native share is only available on Android
 */
export function share(shareData: {
  url: string;
  title?: string;
  text?: string;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    tryNativeMethod({
      callback: () => {
        window.FreedomApp.share(shareData.url);
        resolve();
      },
      errorMessage:
        'Native app does not implement `window.FreedomApp.share` method',
      errorHandler: async () => {
        try {
          await navigator.share(shareData);
          resolve();
        } catch (error) {
          reject(error);
        }
      },
    });
  });
}
